import { Button, SelectInput, SelectInputItem } from '@ads/components-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { SlideCategory } from '../../../store/atoms/slideCategoriesAtom';

export interface LibrarySearchBarProps {
  readonly searchQuery: string;
  readonly selectedCategoryId: number | null;
  readonly categories: SlideCategory[];
  readonly onSearchQueryChange: (value: string) => void;
  readonly onCategoryChange: (categoryId: number | null) => void;
  readonly onSearch: () => void;
}

/**
 * Barra de busca da biblioteca de slides.
 * Filtros são aplicados somente ao clicar em "Buscar" ou pressionar Enter.
 */
export function LibrarySearchBar({
  searchQuery,
  selectedCategoryId,
  categories,
  onSearchQueryChange,
  onCategoryChange,
  onSearch,
}: LibrarySearchBarProps) {
  const { t } = useTranslation('slideEditor');

  return (
    <form
      className="flex flex-wrap items-end gap-nano"
      onSubmit={e => {
        e.preventDefault();
        onSearch();
      }}
    >
      {/* Campo de busca por nome — input nativo para suporte a onKeyDown e ref */}
      <div className="relative flex min-w-[12rem] flex-1 items-center">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-micro h-xxxs w-xxxs text-ctx-content-placeholder" />
        <input
          type="text"
          placeholder={t('library.search.placeholder')}
          value={searchQuery}
          onChange={e => onSearchQueryChange(e.target.value)}
          className="h-[2.5rem] w-full rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface pl-[2rem] pr-micro text-ctx-content-base outline-none ts-paragraph-xs placeholder:text-ctx-content-placeholder focus:border-ctx-interactive-primary"
        />
      </div>

      <div className="w-[14rem]">
        <SelectInput
          label=""
          size="md"
          value={selectedCategoryId?.toString() ?? ''}
          onValueChange={value => onCategoryChange(value === '0' ? null : Number(value))}
          placeholder={t('library.search.allCategories')}
          fullWidth
        >
          <SelectInputItem custom={{ root: 'text-nowrap' }} value="0">
            {t('library.search.allCategories')}
          </SelectInputItem>
          {categories.map(cat => (
            <SelectInputItem
              custom={{ root: 'text-nowrap' }}
              key={cat.id}
              value={cat.id.toString()}
            >
              {cat.name}
            </SelectInputItem>
          ))}
        </SelectInput>
      </div>

      <Button hierarchy="primary" size="md">
        {t('library.search.button')}
      </Button>
    </form>
  );
}
